<div class="usdm-page">
  <header class="page-header">
    <div>
      <h1>USDM Builder</h1>
      <p class="subtitle">Coordinate extraction, generation, and validation of Unified Study Data Model artifacts.</p>
    </div>
    <div class="header-actions">
      <button class="btn-outline" (click)="setStep(1)">Study Creation</button>
      <button class="btn-outline" (click)="setStep(2)">Extraction</button>
      <button class="btn-outline" (click)="setStep(3)">USDM</button>
      <button class="btn-outline" (click)="setStep(4)">Validation</button>
      <button class="btn-primary">Open in OSB</button>
    </div>
  </header>

  <div class="stats-bar">
    <div class="stat">
      <span class="stat-label">Elements processed</span>
      <span class="stat-value">{{ extractedCount }} / {{ catalog.length }}</span>
    </div>
    <div class="stat">
      <span class="stat-label">Validated</span>
      <span class="stat-value success">{{ validatedCount }}</span>
    </div>
  </div>

  <div class="stepper">
    <ng-container *ngFor="let name of steps; let i = index">
      <div class="stepper-item">
        <div
          class="stepper-indicator"
          [ngClass]="{ 'stepper-indicator-active': i + 1 <= step }"
        >
          {{ i + 1 }}
        </div>
        <span
          class="stepper-label"
          [ngClass]="{ 'stepper-label-active': i + 1 <= step }"
        >
          {{ name }}
        </span>
        <div class="stepper-connector" *ngIf="i < steps.length - 1"></div>
      </div>
    </ng-container>
  </div>

  <section *ngIf="step === 1" class="step-section">
    <div class="card">
      <div class="card-header">
        <h2>1.1 New Study Creation</h2>
      </div>
      <div class="card-content form-grid">
        <div class="two-column">
          <label class="field">
            <span>Study Title</span>
            <input class="input" placeholder="e.g., Phase III ABC-123" />
          </label>
          <div class="inline-grid">
            <label class="field">
              <span>Sponsor</span>
              <input class="input" />
            </label>
            <label class="field">
              <span>Phase</span>
              <input class="input" />
            </label>
          </div>
        </div>
        <div class="actions">
          <button class="btn-primary full-width">Create Study</button>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <h2>1.2 Protocol Upload</h2>
      </div>
      <div class="card-content horizontal-actions">
        <button class="btn-outline">
          ⬆ Upload Protocol (PDF)
        </button>
        <button class="btn-primary" (click)="setStep(2)">Proceed to Extraction</button>
      </div>
    </div>
  </section>

  <section *ngIf="step === 2" class="step-section">
    <div class="card">
      <div class="card-header">
        <h2>2. Data Extraction</h2>
      </div>
      <div class="card-content">
        <div class="extraction-header">
          <p>Run the automated pipeline or validate each element individually with traceability.</p>
          <div class="header-actions small">
            <button class="btn-secondary" (click)="runAllElements()">Run All Elements</button>
            <button class="btn-primary" (click)="setStep(3)">Next: USDM Generation</button>
          </div>
        </div>

        <div class="element-grid">
          <div class="element-card" *ngFor="let name of catalog">
            <div class="element-header">
              <h3>{{ name }}</h3>
              <span class="status-chip success" *ngIf="elements[name]?.status === 'validated'">✔ Validated</span>
              <span class="status-chip warning" *ngIf="elements[name]?.status === 'extracted'">● In Review</span>
              <span class="status-chip muted" *ngIf="elements[name]?.status === 'pending'">○ Pending</span>
            </div>

            <div class="progress">
              <div class="progress-bar" [style.width.%]="getProgress(name)"></div>
            </div>

            <div class="element-actions vertical">
              <button
                class="btn-secondary"
                [ngClass]="{ completed: substeps[name] === 'extract' || substeps[name] === 'verify' || substeps[name] === 'approved' }"
                (click)="markExtracted(name)"
              >
                Extract
              </button>
              <button
                class="btn-secondary"
                [disabled]="substeps[name] !== 'extract' && substeps[name] !== 'verify' && substeps[name] !== 'approved'"
                [ngClass]="{ completed: substeps[name] === 'verify' || substeps[name] === 'approved' }"
                (click)="markVerified(name); openPanel(name)"
              >
                Verify
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  </section>

  <section *ngIf="step === 3" class="step-section">
    <div class="card">
      <div class="card-header">
        <h2>3. USDM Generation</h2>
      </div>
      <div class="card-content generation">
        <div class="header-actions small">
          <button class="btn-outline">⬇ Download USDM (JSON)</button>
          <button class="btn-primary" (click)="setStep(4)">Proceed to Validation</button>
        </div>
        <div class="tabs">
         <div class="tab-list">
           <button
             class="tab"
             [ngClass]="{ 'tab-active': generationTab === 'viewer' }"
             (click)="generationTab = 'viewer'"
            >
              Viewer
            </button>
            <button
              class="tab"
              [ngClass]="{ 'tab-active': generationTab === 'raw' }"
              (click)="generationTab = 'raw'"
            >
              Raw JSON
            </button>
          </div>

          <div class="tab-body" *ngIf="generationTab === 'viewer'">
            <div class="viewer-placeholder">
              A simple USDM viewer would render normalized entities here (mock).
            </div>
          </div>

          <div class="tab-body" *ngIf="generationTab === 'raw'">
            <textarea
              class="textarea"
              rows="14"
              [(ngModel)]="usdmJson"
              [ngModelOptions]="{ standalone: true }"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  </section>

</div>

<ng-container *ngIf="active as current">
  <div class="sheet-backdrop" (click)="closePanel()"></div>
  <div class="sheet">
    <div class="sheet-header">
      <div>
        <h2>Element: {{ current.name }}</h2>
        <p>Side-by-side protocol context and extracted data.</p>
      </div>
      <button class="btn-outline small" (click)="closePanel()">Close</button>
    </div>

    <div class="sheet-body">
      <div class="sheet-left">
        <div class="sheet-toolbar">
          <span class="badge">Protocol Viewer</span>
        </div>
        <div class="pdf-viewer">
          <div
            class="pdf-page"
            *ngFor="let page of pdfPreviewPages"
          >
            <span class="pdf-page-number">Page {{ page }}</span>
            <div
              class="highlight"
              *ngFor="let span of current.sourceSpans"
              [style.display]="span.page === page ? 'block' : 'none'"
              [style.top.px]="span.y"
              [style.height.px]="span.height"
              [attr.title]="span.label || 'match'"
            ></div>
          </div>
        </div>
      </div>

      <div class="sheet-right">
        <h3 class="sheet-section-title">Extracted Element</h3>
        <div class="form-grid detail">
          <label class="field">
            <span>Title</span>
            <input
              class="input"
              [(ngModel)]="current.payload.title"
              [ngModelOptions]="{ standalone: true }"
            />
          </label>
          <label class="field">
            <span>Description</span>
            <textarea
              class="textarea"
              rows="6"
              [(ngModel)]="current.payload.description"
              [ngModelOptions]="{ standalone: true }"
            ></textarea>
          </label>
          <div class="inline-grid">
            <label class="field">
              <span>Code</span>
              <input
                class="input"
                [(ngModel)]="current.payload.code"
                [ngModelOptions]="{ standalone: true }"
              />
            </label>
            <label class="field">
              <span>Version</span>
              <input
                class="input"
                [(ngModel)]="current.payload.version"
                [ngModelOptions]="{ standalone: true }"
              />
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="sheet-trace">
      <h3 class="sheet-section-title">Traceability</h3>
      <div class="trace-list">
        <div class="trace-entry" *ngFor="let trace of getTracesForActive(); let idx = index">
          <div class="trace-header">Call #{{ idx + 1 }}</div>
          <div class="trace-section">
            <h4>Request</h4>
            <pre>{{ trace.request }}</pre>
          </div>
          <div class="trace-section">
            <h4>Response</h4>
            <pre>{{ trace.response }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div class="sheet-footer">
      <div class="footer-comment">
        <label for="rerun-comment">Rerun Comment</label>
        <textarea
          id="rerun-comment"
          class="textarea footer-comment-input"
          rows="3"
          placeholder="Explain why this element needs a rerun"
          [(ngModel)]="panelRerunComment"
          [ngModelOptions]="{ standalone: true }"
        ></textarea>
      </div>
      <div class="footer-actions">
        <button class="btn-success" (click)="approveFromPanel()">Approve</button>
        <button
          class="btn-warning"
          (click)="rerunFromPanel()"
          [disabled]="!canSubmitRerun"
        >
          Rerun
        </button>
      </div>
    </div>
  </div>
</ng-container>
